import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Clock, LifeBuoy, Wallet, CalendarCheck, HeartHandshake } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { COMMITMENTS, REGIONS, REGION_FLAGS } from "../data/services";

const COMMITMENT_ICONS = [Users, Clock, LifeBuoy, Wallet, CalendarCheck, HeartHandshake];

const STEPS = [
    { step: "01", title: "Understand the business need", desc: "We begin by listening. Your goals, your current systems, your team and your constraints shape everything that follows — so the solution fits the business, not the other way around." },
    { step: "02", title: "Plan the right solution", desc: "Together we agree what to build, fix or grow — with the scope, technology, timeline and cost laid out clearly before work begins. No surprises halfway through." },
    { step: "03", title: "Deliver with care", desc: "Our specialists build, integrate and test carefully, keeping you updated with visible progress. Go-lives are planned events, not leaps of faith." },
    { step: "04", title: "Support the client after launch", desc: "Launch is a milestone, not a goodbye. We provide post-go-live assistance and stay available — because real value shows up in daily use." },
];

export default function About() {
    return (
        <>
            <Seo
                title="About Us"
                description="How Innovated Global IT Solutions helps businesses connect systems, strengthen digital presence, build software and apply AI — and the way we work."
            />

            <section className="bg-grid relative overflow-hidden py-20 lg:py-24" data-testid="about-hero">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(900px 480px at 80% 0%, rgba(0,102,255,0.14), transparent 60%), radial-gradient(700px 420px at 10% 100%, rgba(230,0,126,0.08), transparent 60%)" }}
                />
                <div className="container-x relative max-w-3xl">
                    <Reveal>
                        <p className="eyebrow text-cyan-300">About us</p>
                        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                            One partner for your systems, your software, your presence and your AI.
                        </h1>
                        <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                            Innovated Global IT Solutions Private Limited is a technology and digital services
                            company built around a simple idea: businesses grow faster when their technology works
                            together. Our four practices — Infor solutions, digital marketing, software development,
                            and AI — are each deep in their own field, and connected at the center.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-400">
                            That is what our logo stands for: four petals around one glowing core. Ideas. Innovation.
                            Impact.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="about-what-we-do">
                <div className="container-x focus-grid grid gap-6 md:grid-cols-2">
                    {[
                        { title: "Connect your systems", desc: "Infor WMS, LN, ION and reporting — implemented, integrated and supported so operations and data flow as one.", accent: "#0066FF" },
                        { title: "Strengthen your digital presence", desc: "Search, social, paid and email programs guided by strategy and measured on performance.", accent: "#E6007E" },
                        { title: "Build the software you need", desc: "Databases, APIs, web applications and custom business tools — engineered around your workflows.", accent: "#7000FF" },
                        { title: "Apply AI where it helps", desc: "Machine learning, generative AI and intelligent automation — practical, measured and honest about fit.", accent: "#00F0FF" },
                    ].map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.06}>
                            <div className="focus-card glass h-full rounded-3xl p-7" style={{ "--accent": c.accent, "--accent-soft": `${c.accent}24` }} data-testid={`about-pillar-${i}`}>
                                <span className="block h-1 w-12 rounded-full" style={{ background: c.accent }} />
                                <h2 className="mt-5 text-xl font-semibold">{c.title}</h2>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-y border-white/5 py-16 lg:py-24" style={{ background: "linear-gradient(135deg, rgba(0,102,255,0.07), rgba(19,26,48,0.55) 42%, rgba(112,0,255,0.07))" }} data-testid="about-approach">
                <div className="container-x">
                    <Reveal>
                        <p className="eyebrow text-violet-400">Our approach</p>
                        <h2 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                            Four steps. No drama.
                        </h2>
                    </Reveal>
                    <div className="focus-grid mt-12 grid gap-6 md:grid-cols-2">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.step} delay={i * 0.07}>
                                <div
                                    className="focus-card glass h-full rounded-3xl p-7"
                                    style={{ "--accent": ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4], "--accent-soft": ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4] + "24" }}
                                    data-testid={`about-step-${s.step}`}
                                >
                                    <span
                                        className="text-4xl font-bold"
                                        style={{ fontFamily: "Outfit, sans-serif", color: "transparent", WebkitTextStroke: "1.5px rgba(148,163,184,0.45)" }}
                                    >
                                        {s.step}
                                    </span>
                                    <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="about-commitments">
                <div className="container-x">
                    <Reveal>
                        <p className="eyebrow text-fuchsia-400">Our commitments</p>
                        <h2 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">
                            How we hold ourselves accountable.
                        </h2>
                    </Reveal>
                    <div className="focus-grid mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {COMMITMENTS.map((c, i) => {
                            const Icon = COMMITMENT_ICONS[i];
                            const accent = ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4];
                            return (
                                <Reveal key={c.title} delay={i * 0.05}>
                                    <div
                                        className="focus-card glass flex h-full gap-4 rounded-3xl p-6"
                                        style={{ "--accent": accent, "--accent-soft": `${accent}24` }}
                                    >
                                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}>
                                            <Icon size={20} aria-hidden="true" />
                                        </span>
                                        <div>
                                            <h3 className="text-base font-semibold">{c.title}</h3>
                                            <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal className="mt-12">
                        <div className="glass flex flex-col items-center gap-5 rounded-3xl p-8 text-center" data-testid="about-regions-panel">
                            <h3 className="text-lg font-semibold">Supporting clients across the globe</h3>
                            <ul className="flex flex-wrap justify-center gap-3">
                                {REGIONS.map((r, i) => (
                                    <li
                                        key={r}
                                        className="region-chip rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-slate-300"
                                        style={{ background: "rgba(255,255,255,0.03)", "--swap-delay": `${i * 0.85}s` }}
                                        data-testid={`about-region-chip-${i}`}
                                    >
                                        <span className="region-name">{r}</span>
                                        <span className="region-flag" aria-hidden="true">{REGION_FLAGS[r]}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/contact" className="btn-primary mt-2" data-testid="about-cta-button">
                                Work with us
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
